import { FC, useMemo } from 'react';
import { useAppSelector } from '../../app/hooks';
import { GamesResponse } from '../../app/api/teamSchedule.api';
import { ScheduleCard } from '../scheduleCard/ScheduleCard';
import { getTeamColors } from '../../constants/teamColors';
import styles from './ScheduleGrid.module.css';

interface ScheduleGridProps {
  selectedTeamIds: number[];
}

export const ScheduleGrid: FC<ScheduleGridProps> = ({ selectedTeamIds }) => {
  const schedule = useAppSelector(state => state.teamSchedule.schedule);

  const { allDates, getGame } = useMemo(() => {
    const dateSet = new Set<string>();
    const gameLookup: Record<string, Record<number, GamesResponse>> = {};

    for (const teamId of selectedTeamIds) {
      const teamSchedule = schedule[teamId as unknown as number];
      if (!teamSchedule?.dates) continue;

      const homeDates = teamSchedule.dates.filter(
        d => d.games[0]?.teams.home.team.id === teamId
      );

      for (const dateEntry of homeDates) {
        const dateKey = dateEntry.games[0].gameDate.split('T')[0];
        dateSet.add(dateKey);
        if (!gameLookup[dateKey]) gameLookup[dateKey] = {};
        gameLookup[dateKey][teamId] = dateEntry.games[0];
      }
    }

    const allDates = Array.from(dateSet).sort();

    const getGame = (date: string, teamId: number): GamesResponse | null =>
      gameLookup[date]?.[teamId] ?? null;

    return { allDates, getGame };
  }, [schedule, selectedTeamIds]);

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return {
      weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
      monthDay: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    };
  };

  const formatTime = (gameDate: string) => {
    const date = new Date(gameDate);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  };

  const colCount = selectedTeamIds.length;

  if (allDates.length === 0) {
    return <div className={styles.empty}>No home games found for the selected teams and date range.</div>;
  }

  return (
    <div
      className={styles.grid}
      style={{ gridTemplateColumns: `80px repeat(${colCount}, 1fr)` }}
    >
      {allDates.map((date, rowIndex) => {
        const { weekday, monthDay } = formatDate(date);
        return (
          <div className={styles.row} key={date}>
            <div
              className={`${styles.dateCell} ${rowIndex % 2 === 0 ? styles.rowEven : styles.rowOdd}`}
            >
              <span className={styles.weekday}>{weekday}</span>
              <span className={styles.monthDay}>{monthDay}</span>
            </div>
            {selectedTeamIds.map(teamId => {
              const game = getGame(date, teamId);
              const colors = getTeamColors(teamId);
              return (
                <div
                  key={`game-${date}-${teamId}`}
                  className={`${styles.gameCell} ${!game ? styles.emptyCell : ''} ${rowIndex % 2 === 0 ? styles.rowEven : styles.rowOdd}`}
                >
                  {game && (
                    <ScheduleCard
                      homeTeam={game.teams.home.team.name}
                      awayTeam={game.teams.away.team.name}
                      gameDate={formatTime(game.gameDate)}
                      teamColor={colors.primary}
                      teamAccent={colors.secondary}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
